import React, { createContext, useContext, useState, useEffect } from 'react';
import io, { Socket } from 'socket.io-client';

import { useAuth } from './auth';

interface SocketIoContextData {
  socket?: Socket;
}

const SocketIoContext = createContext<SocketIoContextData>(
  {} as SocketIoContextData,
);

/** Espera entre tentativas de handshake, dobrando ate o teto. */
const RETRY_BASE_MS = 1000;
const RETRY_MAX_MS = 30000;

/**
 * A conexao de tempo real com o `cinco-back`.
 *
 * ## O que o E2E de 2026-08-28 encontrou aqui
 *
 * Um `socket handshake rejected` no back **a cada login**, e o efeito visivel: a
 * tela de Alertas aberta logo depois de entrar nao recebia nada -- nem o toast de
 * alerta novo, nem a recarga dos contadores. So depois de um F5 o tempo real
 * passava a funcionar.
 *
 * Tres causas somadas, e as tres estao tratadas abaixo:
 *
 * 1. **Conectava sem token.** O socket nascia assim que `user` aparecia e o token
 *    era lido do `localStorage`, que numa corrida com a gravacao dele ainda podia
 *    estar vazio. O `verifyCodeMFA` agora grava o token *antes* de publicar o
 *    usuario, e aqui o efeito **nao conecta** se o token nao estiver la.
 * 2. **Nao havia retentativa.** Erro de *middleware* -- que e como a guarda do
 *    handshake recusa -- desliga o `reconnection` do socket.io: o cliente desiste
 *    em silencio e a aba fica muda ate alguem recarregar. Agora um handshake
 *    recusado e observado e refeito, com espera crescente e teto.
 * 3. **Nada era desconectado.** Sem cleanup, sair e entrar de novo deixava o socket
 *    velho pendurado e a aba acumulava conexoes.
 *
 * ⓘ **O token vai em `auth`, alem do header.** `extraHeaders` so viaja no
 * transporte de *polling*; se a conexao for promovida a WebSocket, o header some. O
 * `extractToken` do back le `handshake.auth.token` primeiro -- mandar nos dois e o
 * que faz a autenticacao sobreviver ao upgrade de transporte.
 */
export const SocketIoProvider: React.FC = ({ children }) => {
  const { user } = useAuth();

  const [socket, setSocket] = useState<Socket>();

  useEffect(() => {
    const baseUrl = process.env.REACT_APP_API_BASE_URL;

    if (!user || !baseUrl) {
      return undefined;
    }

    const token = localStorage.getItem('@Cinco:token');

    if (!token) {
      // Sem token o handshake seria recusado e o socket desistiria para sempre.
      // Nao conectar e melhor: quando o token chegar, `user` muda e este efeito
      // roda de novo.
      return undefined;
    }

    const socketIo = io(baseUrl, {
      auth: { token: `Bearer ${token}` },
      extraHeaders: { authorization: `Bearer ${token}` },
    });

    let retryTimer: ReturnType<typeof setTimeout> | undefined;
    let attempt = 0;
    let closed = false;

    const identify = () => {
      socketIo.emit('setExternalId', { externalId: user.id });
    };

    const handleConnect = () => {
      attempt = 0;
      identify();
    };

    /**
     * ⚠️ Erro de conexao **nao** e retomado sozinho quando vem de um middleware do
     * servidor (`socketIo.active` fica falso). Este handler e o que separa "o back
     * caiu, o socket.io se vira" de "o handshake foi recusado e ninguem vai tentar
     * de novo".
     */
    const handleConnectError = () => {
      if (closed || socketIo.active) {
        return;
      }

      attempt += 1;

      const delay = Math.min(RETRY_BASE_MS * 2 ** (attempt - 1), RETRY_MAX_MS);

      retryTimer = setTimeout(() => {
        if (!closed) {
          socketIo.connect();
        }
      }, delay);
    };

    socketIo.on('connect', handleConnect);
    socketIo.on('connect_error', handleConnectError);

    identify();
    setSocket(socketIo);

    return () => {
      closed = true;

      if (retryTimer) {
        clearTimeout(retryTimer);
      }

      socketIo.off('connect', handleConnect);
      socketIo.off('connect_error', handleConnectError);
      socketIo.disconnect();
    };
  }, [user]);

  return (
    <SocketIoContext.Provider
      value={{
        socket,
      }}
    >
      {children}
    </SocketIoContext.Provider>
  );
};

export const useSocketIo = (): SocketIoContextData => {
  const context = useContext(SocketIoContext);

  if (!context) {
    throw new Error('useSocketIo must be used within a SocketIoProvider');
  }

  return context;
};
